import axiosInstance from './axiosConfig';

/**
 * Restaurant-specific API service for POS operations
 */
const restaurantAPI = {
  // Cart operations
  carts: {
    create: (cartData) => axiosInstance.post('/carts', cartData),
    getById: (cartId) => axiosInstance.get(`/carts/${cartId}`),
    update: (cartId, cartData) => axiosInstance.put(`/carts/${cartId}`, cartData),
    delete: (cartId) => axiosInstance.delete(`/carts/${cartId}`),
    getByTable: (tableNumber) =>
      axiosInstance.get(`/carts/table/${tableNumber}`),
    cancelPrintedItem: (cartId, productId) =>
      axiosInstance.post(
        '/carts/cancel-printed-item',
        { cartId, productId }
      )
  },

  // Shift operations (Restaurant-specific)
  shifts: {
    open: (cashierId, pin, startingDenominations) =>
      axiosInstance.post(
        '/restaurant/shift/open',
        { 
          cashierId, 
          pin, 
          startingDenominations 
        }
      ),
    close: (shiftId, cashierPin, closingDenominations, varianceNote) =>
      axiosInstance.post(
        `/restaurant/shift/${shiftId}/close`,
        { 
          cashierPin, 
          closingDenominations,
          varianceNote 
        }
      ),
    getActive: () =>
      axiosInstance.get('/restaurant/shift/active'),
    getActiveByCashier: (cashierId) =>
      axiosInstance.get(`/restaurant/shift/active-by-cashier/${encodeURIComponent(cashierId)}`),
    // Verify PIN for session re-authentication (timeout recovery)
    verifyPin: (shiftId, pin) =>
      axiosInstance.post(
        `/restaurant/shift/${shiftId}/verify-pin`,
        { pin }
      ),
    // Verify waiter credentials
    verifyWaiter: (waiterId, pin) =>
      axiosInstance.post(
        '/restaurant/shift/verify-waiter',
        { waiterId, pin }
      ),
    // POS-namespace close with full denomination declaration (CloseShiftModal)
    getSummary: (shiftId) =>
      axiosInstance.get(`/pos/restaurant/shifts/${shiftId}/summary`),
    closeWithDenominations: (shiftId, denominations, closingNotes) =>
      axiosInstance.patch(
        `/pos/restaurant/shifts/${shiftId}/close`,
        { denominations, closingNotes }
      ),
    printReport: (shiftId, denominations = []) =>
      axiosInstance.post(
        `/pos/restaurant/shifts/${shiftId}/print-report`,
        { denominations }
      ),
    // Legacy endpoints for backward compatibility
    start: (startingCash = 0) =>
      axiosInstance.post(
        '/shift/start',
        { startingCash }
      ),
    end: (shiftId, endingCash) =>
      axiosInstance.post(
        '/shift/end',
        { shiftId, endingCash }
      )
  },

  // Printing operations
  printing: {
    printKitchen: (cartId, items = null) =>
      axiosInstance.post(
        '/print/kitchen',
        { cartId, items }
      ),
    printBar: (cartId, items = null) =>
      axiosInstance.post(
        '/print/bar',
        { cartId, items }
      ),
    printReceipt: (cartId) =>
      axiosInstance.post(
        '/print-receipt',
        { cartId }
      ),
    printBill: (tableId) =>
      axiosInstance.post(
        '/print-billing',
        { tableId }
      ),
    printBoth: (cartId) =>
      axiosInstance.post(
        '/print/both',
        { cartId }
      )
  },

  // Order operations (Restaurant-specific)
  orders: {
    create: (tableId, waiterId, waiterPin, products) =>
      axiosInstance.post(
        '/restaurant/orders',
        { tableId, waiterId, waiterPin, products }
      ),
    addItems: (orderId, products) =>
      axiosInstance.post(
        `/restaurant/orders/${orderId}/items`,
        { products }
      ),
    send: (orderId) =>
      axiosInstance.post(
        `/restaurant/orders/${orderId}/send`
      ),
    voidItem: (orderId, orderItemId, reason, supervisorPin) =>
      axiosInstance.post(
        `/restaurant/orders/${orderId}/void-item`,
        { orderItemId, reason, supervisorPin }
      ),
    getById: (orderId) =>
      axiosInstance.get(`/restaurant/orders/${orderId}`),
    // Legacy endpoints for backward compatibility
    createFromCart: (cartId, paymentData) =>
      axiosInstance.post(
        `/orders/from-cart/${cartId}`,
        paymentData
      ),
    getByTable: (tableNumber) =>
      axiosInstance.get(`/orders/table/${tableNumber}`)
  },

  // Table operations (Restaurant-specific)
  tables: {
    getAll: () => axiosInstance.get('/restaurant/tables'),
    create: (tableData) =>
      axiosInstance.post('/restaurant/tables', tableData),
    move: (tableId, targetTableId, authorizedByPin) =>
      axiosInstance.post(
        `/restaurant/tables/${tableId}/move`,
        { targetTableId, authorizedByPin }
      ),
    merge: (tableId, targetTableId, authorizedByPin) =>
      axiosInstance.post(
        `/restaurant/tables/${tableId}/merge`,
        { targetTableId, authorizedByPin }
      ),
    transferWaiter: (tableId, newWaiterId, cashierPin) =>
      axiosInstance.post(
        `/restaurant/tables/${tableId}/transfer-waiter`,
        { newWaiterId, cashierPin }
      ),
    // Legacy endpoints for backward compatibility
    getByStore: (storeId) =>
      axiosInstance.get('/tables', { params: { store: storeId } }),
    getStatus: (tableNumber) =>
      axiosInstance.get(`/tables/status/${tableNumber}`),
    updateStatus: (tableNumber, status) =>
      axiosInstance.patch(
        `/tables/${tableNumber}`,
        { status }
      ),
    mergeTables: (sourceTables, targetTable) =>
      axiosInstance.post(
        '/tables/merge',
        { sourceTables, targetTable }
      ),
    splitTable: (tableNumber, cartMetrics) =>
      axiosInstance.post(
        '/tables/split',
        { tableNumber, cartMetrics }
      )
  },

  employees: {
    getWaiters: () =>
      axiosInstance.get('/users', {
        params: {
          role: 'waiter'
        }
      })
  },

  // Billing operations (Restaurant-specific)
  billing: {
    generate: (orderId) =>
      axiosInstance.post(
        '/restaurant/billing/generate',
        { orderId }
      ),
    applyDiscount: (billingId, discountType, idNumber, beneficiaryName, numBeneficiaries, eligibleItemIds) =>
      axiosInstance.post(
        `/restaurant/billing/${billingId}/discount`,
        { 
          discountType, 
          idNumber, 
          beneficiaryName, 
          numBeneficiaries, 
          eligibleItemIds 
        }
      ),
    split: (billingId, mode, splitData) =>
      axiosInstance.post(
        `/restaurant/billing/${billingId}/split`,
        { mode, splitData }
      ),
    processPay: (billingId, cashierPin, payments, compData) =>
      axiosInstance.post(
        `/restaurant/billing/${billingId}/pay`,
        { 
          cashierPin, 
          payments, 
          compData 
        }
      ),
    getById: (billingId) =>
      axiosInstance.get(`/restaurant/billing/${billingId}`)
  }
};

export default restaurantAPI;
